import { useState } from 'react'
import {
  generateEquations,
  type EquationsAnswer,
  type EquationsPrompt,
} from '../generators/equations.ts'
import { generateFigures, type FiguresAnswer, type FiguresPrompt } from '../generators/figures.ts'
import { generateLatin, type LatinAnswer as LatinAnswerData, type LatinPrompt } from '../generators/latin.ts'
import { QuestionCard } from '../render/QuestionCard.tsx'
import { EquationsInputs, EquationsSystem } from '../render/Equations.tsx'
import { LatinAnswer, LatinGrid } from '../render/LatinGrid.tsx'
import { FiguresOptions } from '../render/Figures.tsx'
import { FiguresStem } from '../render/FigureMatrix.tsx'
import { gradeEquations, gradeLatin } from '../render/grade.ts'

/** Dev preview of the M4 render + answer UI (PLAN-Z M4 verify aid): one live
 * equations item and one live Latin item at /preview. Not a practice mode —
 * Drill arrives in M5. */

function EquationsPreviewItem({ seed }: { seed: number }) {
  const q = generateEquations('medium', seed)
  const prompt = q.prompt as EquationsPrompt
  const answer = (q.answer as EquationsAnswer).values
  const [values, setValues] = useState<Record<string, string>>({})
  const [perLetter, setPerLetter] = useState<Record<string, boolean> | null>(null)
  const complete = prompt.letters.every((L) => values[L] !== undefined && values[L] !== '')
  const locked = perLetter !== null
  const submit = () => {
    if (!complete || locked) return
    setPerLetter(gradeEquations(prompt, answer, values).perLetter)
  }
  const allCorrect = perLetter !== null && prompt.letters.every((L) => perLetter[L])
  return (
    <QuestionCard
      title="Mathematical Equations"
      prompt={<EquationsSystem prompt={prompt} />}
      answer={
        <EquationsInputs
          prompt={prompt}
          values={values}
          perLetterCorrect={perLetter}
          onChange={(letter, value) => setValues((prev) => ({ ...prev, [letter]: value }))}
          onSubmitLast={submit}
        />
      }
      onSubmit={submit}
      submitDisabled={!complete || locked}
      feedback={
        perLetter ? <p>{allCorrect ? 'Correct.' : 'One or more letters are wrong.'}</p> : undefined
      }
    />
  )
}

function LatinPreviewItem({ seed }: { seed: number }) {
  const q = generateLatin('medium', seed)
  const prompt = q.prompt as LatinPrompt
  const answer = (q.answer as LatinAnswerData).letter
  const [selected, setSelected] = useState<string | null>(null)
  const [graded, setGraded] = useState<boolean | null>(null)
  const submit = () => {
    if (selected === null) return
    setGraded(gradeLatin(answer, selected))
  }
  return (
    <QuestionCard
      title="Latin Squares"
      prompt={<LatinGrid prompt={prompt} />}
      answer={
        <LatinAnswer
          selected={selected}
          onSelect={setSelected}
          disabled={graded !== null}
          correctLetter={graded === null ? null : answer}
        />
      }
      onSubmit={submit}
      submitDisabled={selected === null || graded !== null}
      feedback={
        graded === null ? undefined : <p>{graded ? 'Correct.' : `Wrong — the answer is ${answer}.`}</p>
      }
    />
  )
}

function FiguresPreviewItem({ seed }: { seed: number }) {
  const q = generateFigures('medium', seed)
  const prompt = q.prompt as FiguresPrompt
  const answer = (q.answer as FiguresAnswer).correct
  const [picks, setPicks] = useState<[number | null, number | null]>([null, null])
  const [graded, setGraded] = useState<boolean | null>(null)
  const submit = () => {
    if (picks[0] === null || picks[1] === null) return
    setGraded(picks[0] === answer[0] && picks[1] === answer[1])
  }
  const verdict = graded === null ? null : graded ? 'Correct.' : `Wrong — ${answer[0] + 1} then ${answer[1] + 1}.`
  return (
    <QuestionCard
      title="Figure Sequences"
      prompt={<FiguresStem prompt={prompt} />}
      answer={
        <FiguresOptions
          options={prompt.options}
          picks={picks}
          disabled={graded !== null}
          correct={graded === null ? null : answer}
          onPick={(blank, index) =>
            setPicks((prev) => {
              const next: [number | null, number | null] = [...prev]
              next[blank] = index
              return next
            })
          }
        />
      }
      onSubmit={submit}
      submitDisabled={picks[0] === null || picks[1] === null || graded !== null}
      feedback={verdict ? <p>{verdict}</p> : undefined}
    />
  )
}

export function PreviewPage() {
  const [seed, setSeed] = useState(1)
  return (
    <div className="preview-page">
      <p className="preview-note">
        Render preview (dev tool, PLAN-Z M4).{' '}
        <button
          type="button"
          className="preview-next"
          onClick={() => setSeed((s) => s + 1)}
        >
          Next questions →
        </button>
      </p>
      <EquationsPreviewItem key={`eq-${seed}`} seed={seed} />
      <LatinPreviewItem key={`la-${seed}`} seed={seed} />
      <FiguresPreviewItem key={`fig-${seed}`} seed={seed} />
    </div>
  )
}
